import { useEffect, useMemo, useRef } from 'react';
import { Draggable } from '@fullcalendar/interaction';

import { cn } from '@/lib/cn';
import { DRAG_LIFT_SCALE, SNAP_MINUTES } from '@/lib/motion';
import { formatDuration } from '@/features/tasks/tree';
import type { Client, Project, Task } from '@/features/tasks/types';
import {
  BACKLOG_SORTS,
  DUE_PILL,
  dueInfo,
  groupBacklog,
  type BacklogSort,
} from './backlogSort';

interface Props {
  tasks: Task[];
  clients: Client[];
  projects: Project[];
  timezone: string;
  sort: BacklogSort;
  onSortChange: (sort: BacklogSort) => void;
  clientColor: (clientId: number) => string;
  onSchedule: (task: Task) => void;
  selectedTaskId: number | null;
  onSelectTask: (task: Task) => void;
}

/**
 * Backlog chips for the Plan sidebar.
 *
 * Grouped rather than flat: while planning a week the question is not "which
 * is sooner" but "does this have to land before Thursday", and a group header
 * answers that without the user having to read every date.
 *
 * FullCalendar's Draggable wires external drops onto the time grid. Lift and
 * grab-offset come from FC's mirror; we only mark chips and expose duration.
 */
export function PlanBacklog({
  tasks,
  clients,
  projects,
  timezone,
  sort,
  onSortChange,
  clientColor,
  onSchedule,
  selectedTaskId,
  onSelectTask,
}: Props) {
  const listRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = listRef.current;
    if (!el) return;

    const draggable = new Draggable(el, {
      itemSelector: '[data-plan-chip]',
      eventData(eventEl) {
        const minutes = Number(eventEl.dataset.minutes) || SNAP_MINUTES * 2;
        return {
          title: eventEl.dataset.title ?? 'Task',
          duration: { minutes },
          create: false,
        };
      },
    });

    return () => draggable.destroy();
  }, []);

  const groups = useMemo(
    () => groupBacklog(tasks, sort, { timezone, clients, projects }),
    [tasks, sort, timezone, clients, projects],
  );

  return (
    <div ref={listRef} className="flex flex-col gap-3">
      <label className="flex items-center gap-2 text-small text-text-secondary">
        <span className="shrink-0">Sort</span>
        <select
          value={sort}
          onChange={(e) => onSortChange(e.target.value as BacklogSort)}
          className={cn(
            'min-h-touch min-w-0 flex-1 rounded border border-border bg-surface-1 px-2',
            'text-small text-text outline-none focus-visible:ring-2 focus-visible:ring-ring focus:border-accent-border',
          )}
        >
          {BACKLOG_SORTS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>

      {tasks.length === 0 ? (
        <p className="px-1 text-small text-text-secondary">
          Nothing to schedule. Capture a task from Backlog first, or drag a
          block here to unschedule it.
        </p>
      ) : (
        groups.map((group) => (
          <section key={group.key} aria-label={group.label}>
            <h3
              className={cn(
                'sticky top-0 z-sticky mb-1.5 flex items-baseline justify-between gap-2',
                'bg-bg py-1 text-label uppercase',
                group.tone === 'overdue' && 'text-danger',
                group.tone === 'today' && 'text-warning',
                group.tone !== 'overdue' && group.tone !== 'today' && 'text-text-secondary',
              )}
            >
              <span className="flex min-w-0 items-center gap-1.5">
                {group.color && (
                  <span
                    aria-hidden
                    className="size-dot shrink-0 rounded-full"
                    style={{ backgroundColor: group.color }}
                  />
                )}
                <span className="truncate">{group.label}</span>
              </span>
              <span className="shrink-0 tabular font-normal text-text-tertiary">
                {group.meta}
              </span>
            </h3>

            <ul className="flex flex-col gap-1.5">
              {group.tasks.map((task) => {
                const minutes = task.estimate_minutes ?? SNAP_MINUTES * 2;
                const selected = selectedTaskId === task.id;
                const due = dueInfo(task.due_at, timezone);
                const duration = formatDuration(minutes) ?? `${minutes}m`;
                return (
                  <li key={task.id}>
                    <div
                      data-plan-chip
                      data-task-id={task.id}
                      data-title={task.title}
                      data-minutes={minutes}
                      tabIndex={0}
                      role="button"
                      aria-pressed={selected}
                      aria-label={`${task.title}, ${duration}${due.at ? `, ${due.full}` : ''}. Click for details, drag onto the calendar, or press S to pick a time.`}
                      onClick={() => onSelectTask(task)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                          e.preventDefault();
                          onSelectTask(task);
                        }
                        if (e.key === 's' || e.key === 'S') {
                          e.preventDefault();
                          onSelectTask(task);
                          onSchedule(task);
                        }
                      }}
                      className={cn(
                        'plan-task-chip group block cursor-grab rounded border border-border',
                        'bg-surface-1 px-3 py-2 active:cursor-grabbing',
                        'border-l-[3px] outline-none focus-visible:ring-2 focus-visible:ring-ring',
                        selected && 'ring-2 ring-ring',
                      )}
                      style={{
                        borderLeftColor: clientColor(task.client_id),
                        // FullCalendar mirror copies inline styles; scale is applied via CSS class.
                      }}
                    >
                      <p className="truncate text-small font-medium leading-tight text-text">
                        {task.title}
                      </p>
                      <p className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1 text-small leading-none tabular text-text-tertiary">
                        <span>{duration}</span>
                        {due.at && (
                          <span
                            title={due.full}
                            className={cn(
                              'rounded-sm px-1.5 py-0.5 font-medium',
                              DUE_PILL[due.tone],
                            )}
                          >
                            {due.label}
                          </span>
                        )}
                      </p>
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}
    </div>
  );
}

/** CSS hook: lift the FC drag mirror while an external chip is in flight. */
export const planChipDragStyles = `
  .fc-event-mirror,
  .plan-task-chip.fc-event-dragging {
    transform: scale(${DRAG_LIFT_SCALE});
    box-shadow: var(--shadow-lifted);
    z-index: 50;
  }
  .chronos-block-proposed {
    opacity: 0.55;
    border-style: dashed !important;
    border-width: 1.5px !important;
  }
  .plan-blocker-chip.fc-event-dragging {
    transform: scale(${DRAG_LIFT_SCALE});
    box-shadow: var(--shadow-lifted);
  }
  .chronos-block-conflict {
    outline: 2px solid var(--danger);
    outline-offset: 1px;
  }
  .chronos-block-late {
    outline: 2px dashed var(--warning);
    outline-offset: -2px;
  }
`;
